/**
 * @fileoverview DH PNR Generator — standalone module
 * Styled to match Report Logs. Two tabs: Send DHD & My Bookings.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Paper, TextField, Button, Select, MenuItem,
  FormControl, InputLabel, Alert, Chip, IconButton,
  FormControlLabel, Switch, Typography, Stack, Fade, Grow,
  Tab, Tabs, Divider, CircularProgress, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  PlaneTakeoff, Plane, Send, Search, Copy, CheckCircle,
  Ticket, RefreshCw, List,
} from 'lucide-react';
import { pnrApi } from '@/features/PnrGenerator/services/pnrApi';
import { AirlineLoader } from '@/features/PnrGenerator/components/AirlineLoader';

/* ── Shared sx tokens ── */
const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px', bgcolor: '#fff',
    '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(0,151,169,0.10)' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#0097A9' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0097A9', borderWidth: 2 },
};
const selectSx = { ...inputSx, '& .MuiSelect-select': { fontSize: '0.875rem' } };

/* ── Constants ── */
const DHD_POSITIONS = [
  { value: 'CA', label: 'CA — Captain' },
  { value: 'FO', label: 'FO — First Officer' },
  { value: 'FA', label: 'FA — Flight Attendant (Lead)' },
  { value: 'FAL', label: 'FAL — Flight Attendant (Line)' },
  { value: 'FAP', label: 'FAP — Flight Attendant (Purser)' },
];
const DHD_AIRLINES = [
  { value: 'AS', label: 'AS — Alaska Airlines' },
  { value: 'QX', label: 'QX — Horizon Air' },
  { value: 'OO', label: 'OO — SkyWest' },
];

/* ── Helpers ── */
function CopyBtn({ text }) {
  const [ok, setOk] = useState(false);
  const copy = () => { navigator.clipboard.writeText(text); setOk(true); setTimeout(() => setOk(false), 1500); };
  return (
    <Tooltip title={ok ? 'Copied!' : 'Copy'}>
      <IconButton size="small" onClick={copy} sx={{ '&:hover': { bgcolor: 'rgba(0,151,169,0.08)' } }}>
        {ok ? <CheckCircle size={14} color="#00AF66" /> : <Copy size={14} color="#64748b" />}
      </IconButton>
    </Tooltip>
  );
}

const statusChip = (status) => {
  const map = {
    sent: { color: '#2563eb', bg: '#eff6ff', label: 'Sent' },
    processing: { color: '#d97706', bg: '#fffbeb', label: 'Processing' },
    Confirmed: { color: '#16a34a', bg: '#f0fdf4', label: 'Confirmed' },
    confirmed: { color: '#16a34a', bg: '#f0fdf4', label: 'Confirmed' },
    rejected: { color: '#dc2626', bg: '#fef2f2', label: 'Rejected' },
    not_found: { color: '#64748b', bg: '#f8fafc', label: 'Pending' },
  };
  const s = map[status] || { color: '#64748b', bg: '#f8fafc', label: status || '—' };
  return <Chip label={s.label} size="small" sx={{ bgcolor: s.bg, color: s.color, fontWeight: 600, fontSize: '0.75rem', border: `1px solid ${s.color}22` }} />;
};

/* ══════════════════════════════════════
   SEND DHD TAB
   ══════════════════════════════════════ */
function SendDhdTab({ onSent }) {
  const [form, setForm] = useState({
    flight_number: '', flight_date_lt: '', origin_station: '', destination_station: '',
    airline_code: 'AS', position: 'FA', request_type: 'Initial',
    crew_id: '', seniority_number: '',
    duration_hours: '', duration_minutes: '',
    working_flight_in_same_duty: false, dh_directly_follows: false, dh_directly_precedes: false,
    segment_no_of_flight: '1', pnr: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState(null);

  // Flight lookup
  const [flightStatus, setFlightStatus] = useState(null);
  const [availableFlights, setAvailableFlights] = useState([]);
  const flightCheckRef = useRef(null);

  const upd = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (flightCheckRef.current) clearTimeout(flightCheckRef.current);
    const { flight_number: fn, flight_date_lt: dt, origin_station: orig, destination_station: dest } = form;
    if (!dt || orig.length < 3 || dest.length < 3) { setFlightStatus(null); setAvailableFlights([]); return; }
    setFlightStatus('searching');
    flightCheckRef.current = setTimeout(async () => {
      try {
        const res = await pnrApi.findFlights({ departureAirportCode: orig, arrivalAirportCode: dest, departureDateTimeStart: dt, classOfService: 'Y', numberOfPassengers: 1 });
        const flights = Array.isArray(res) ? res : (res?.flights || res?.data || []);
        const getNum = f => typeof f === 'string' ? f.split(' - ')[0].trim() : String(f.FlightNumber || f.flightNumber || f.flight_number || '');
        if (!fn) {
          setFlightStatus('available');
          setAvailableFlights(flights.slice(0, 10));
        } else if (flights.some(f => getNum(f) === String(fn))) {
          setFlightStatus('valid');
          setAvailableFlights([]);
        } else {
          setFlightStatus('invalid');
          setAvailableFlights(flights.slice(0, 8));
        }
      } catch {
        setFlightStatus(fn ? 'invalid' : null);
        setAvailableFlights([]);
      }
    }, 500);
    return () => { if (flightCheckRef.current) clearTimeout(flightCheckRef.current); };
  }, [form.flight_number, form.flight_date_lt, form.origin_station, form.destination_station]);

  const buildDuration = () => {
    const h = parseInt(form.duration_hours) || 0;
    const m = parseInt(form.duration_minutes) || 0;
    if (!h && !m) return '';
    let d = 'PT'; if (h) d += `${h}H`; if (m) d += `${m}M`; return d;
  };

  const canSend = form.flight_date_lt && form.origin_station && form.destination_station &&
    (parseInt(form.duration_hours) > 0 || parseInt(form.duration_minutes) > 0) &&
    (!form.crew_id || form.seniority_number) &&
    (form.request_type !== 'Update' || form.pnr);

  const handleSend = async () => {
    setError(''); setLoading(true); setResult(null); setCheckResult(null);
    const duration = buildDuration();
    const payload = {
      flight_date_lt: form.flight_date_lt,
      origin_station: form.origin_station.toUpperCase(),
      destination_station: form.destination_station.toUpperCase(),
      airline_code: form.airline_code,
      position: form.position,
      request_type: form.request_type,
      duration_of_dh: duration,
      working_flight_in_same_duty: form.working_flight_in_same_duty,
      dh_directly_follows: form.dh_directly_follows,
      dh_directly_precedes: form.dh_directly_precedes,
      total_dh_duration_in_duty: duration,
      segment_no_of_flight: parseInt(form.segment_no_of_flight) || 1,
    };
    if (form.flight_number) payload.flight_number = parseInt(form.flight_number);
    if (form.crew_id) { payload.crew_id = form.crew_id; payload.seniority_number = parseInt(form.seniority_number); }
    if (form.request_type === 'Update' && form.pnr) payload.pnr = form.pnr;
    try {
      const res = await pnrApi.sendDhdMessage(payload);
      setResult(res);
      if (onSent) onSent();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to send');
    }
    setLoading(false);
  };

  const handleCheck = async () => {
    if (!result?.jct_ticket_reference) return;
    setChecking(true); setCheckResult(null);
    try {
      const res = await pnrApi.checkDhdResult({ jct_ticket_reference: result.jct_ticket_reference, airline_code: form.airline_code, position: form.position });
      setCheckResult(res);
    } catch (e) { setCheckResult({ status: 'error', error: e?.response?.data?.error || e.message }); }
    setChecking(false);
  };

  const fieldGrid = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2 };

  return (
    <Box>
      <AirlineLoader active={loading} text="Sending DHD message" />

      {/* Flight Details */}
      <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#01426A', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1.5, mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Plane size={15} color="#0097A9" /> Flight Details
      </Typography>
      <Box sx={{ ...fieldGrid, mb: 1 }}>
        <TextField label="Origin (IATA) *" value={form.origin_station} onChange={e => upd('origin_station', e.target.value.toUpperCase().slice(0, 3))} fullWidth sx={inputSx} placeholder="SJD" inputProps={{ maxLength: 3 }} />
        <TextField label="Destination (IATA) *" value={form.destination_station} onChange={e => upd('destination_station', e.target.value.toUpperCase().slice(0, 3))} fullWidth sx={inputSx} placeholder="SAN" inputProps={{ maxLength: 3 }} />
        <TextField label="Flight Date *" type="date" value={form.flight_date_lt} onChange={e => upd('flight_date_lt', e.target.value)} InputLabelProps={{ shrink: true }} fullWidth sx={inputSx} />
      </Box>
      <Box sx={{ ...fieldGrid, mb: 0.5 }}>
        <Box>
          <TextField label="Flight Number" value={form.flight_number} type="number" onChange={e => upd('flight_number', e.target.value)} fullWidth sx={inputSx} placeholder="e.g. 1395" />
          {flightStatus === 'valid' && (
            <Typography sx={{ fontSize: '0.75rem', color: '#16a34a', fontWeight: 600, mt: 0.5, ml: 0.5 }}>✓ Valid flight</Typography>
          )}
        </Box>
        <FormControl fullWidth sx={selectSx}>
          <InputLabel>Airline</InputLabel>
          <Select value={form.airline_code} label="Airline" onChange={e => upd('airline_code', e.target.value)}>
            {DHD_AIRLINES.map(a => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl fullWidth sx={selectSx}>
          <InputLabel>Position</InputLabel>
          <Select value={form.position} label="Position" onChange={e => upd('position', e.target.value)}>
            {DHD_POSITIONS.map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {/* Flight hints — fixed height to prevent layout shift */}
      <Box sx={{ minHeight: 36, mb: 1 }}>
        {flightStatus === 'searching' && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
            <CircularProgress size={14} sx={{ color: '#0097A9' }} />
            <Typography sx={{ fontSize: '0.75rem', color: '#0097A9', fontWeight: 500 }}>Searching flights…</Typography>
          </Stack>
        )}
        {(flightStatus === 'available' || flightStatus === 'invalid') && availableFlights.length > 0 && (
          <Box sx={{ py: 0.5 }}>
            <Typography sx={{ fontSize: '0.7rem', color: '#64748b', mb: 0.5 }}>
              {flightStatus === 'invalid' ? '✗ Flight not found. Available:' : 'Available flights:'}
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {availableFlights.map((f, i) => {
                const label = typeof f === 'string' ? f : `${f.FlightNumber || f.flightNumber || ''}`;
                const fNum = typeof f === 'string' ? f.split(' - ')[0].trim() : String(f.FlightNumber || f.flightNumber || '');
                return (
                  <Chip key={i} label={label} size="small" onClick={() => upd('flight_number', fNum)}
                    sx={{ fontSize: '0.7rem', fontWeight: 500, cursor: 'pointer', bgcolor: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe', '&:hover': { bgcolor: '#dbeafe' } }} />
                );
              })}
            </Stack>
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Crew & Request */}
      <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#01426A', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Ticket size={15} color="#0097A9" /> Crew & Request
      </Typography>
      <Box sx={{ ...fieldGrid, mb: 2 }}>
        <FormControl fullWidth sx={selectSx}>
          <InputLabel>Request Type</InputLabel>
          <Select value={form.request_type} label="Request Type" onChange={e => upd('request_type', e.target.value)}>
            <MenuItem value="Initial">Initial (New Booking)</MenuItem>
            <MenuItem value="Update">Update (Re-booking)</MenuItem>
          </Select>
        </FormControl>
        <TextField label="Crew ID" value={form.crew_id} onChange={e => upd('crew_id', e.target.value)} fullWidth sx={inputSx} placeholder="Optional" />
        {form.crew_id && (
          <TextField label="Seniority Number *" value={form.seniority_number} type="number" onChange={e => upd('seniority_number', e.target.value)} fullWidth sx={inputSx} placeholder="e.g. 3197" />
        )}
        {form.request_type === 'Update' && (
          <TextField label="Existing PNR *" value={form.pnr} onChange={e => upd('pnr', e.target.value.toUpperCase())} fullWidth sx={inputSx} placeholder="ABCDEF" inputProps={{ maxLength: 6 }} />
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Duration & Duty */}
      <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#01426A', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <PlaneTakeoff size={15} color="#0097A9" /> Duration & Duty
      </Typography>
      <Box sx={{ ...fieldGrid, mb: 2 }}>
        <TextField label="Duration Hours *" value={form.duration_hours} type="number" onChange={e => upd('duration_hours', e.target.value)} fullWidth sx={inputSx} placeholder="2" inputProps={{ min: 0, max: 24 }} />
        <TextField label="Duration Minutes *" value={form.duration_minutes} type="number" onChange={e => upd('duration_minutes', e.target.value)} fullWidth sx={inputSx} placeholder="30" inputProps={{ min: 0, max: 59 }} />
        <TextField label="Segment No." value={form.segment_no_of_flight} type="number" onChange={e => upd('segment_no_of_flight', e.target.value)} fullWidth sx={inputSx} inputProps={{ min: 1 }} />
      </Box>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
        <FormControlLabel control={<Switch checked={form.working_flight_in_same_duty} onChange={e => upd('working_flight_in_same_duty', e.target.checked)} size="small" />}
          label={<Typography sx={{ fontSize: '0.8rem' }}>Working flight in same duty</Typography>} />
        <FormControlLabel control={<Switch checked={form.dh_directly_follows} onChange={e => upd('dh_directly_follows', e.target.checked)} size="small" />}
          label={<Typography sx={{ fontSize: '0.8rem' }}>DH directly follows</Typography>} />
        <FormControlLabel control={<Switch checked={form.dh_directly_precedes} onChange={e => upd('dh_directly_precedes', e.target.checked)} size="small" />}
          label={<Typography sx={{ fontSize: '0.8rem' }}>DH directly precedes</Typography>} />
      </Stack>

      {/* Send */}
      <Button variant="contained" onClick={handleSend} disabled={loading || !canSend} startIcon={<Send size={16} />}
        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', bgcolor: '#01426A', px: 4, py: 1.2,
          '&:hover': { bgcolor: '#002F5F', boxShadow: '0 4px 14px rgba(1,66,106,0.3)' }, '&.Mui-disabled': { bgcolor: '#94a3b8', color: '#fff' } }}>
        {loading ? 'Sending…' : 'Send DHD Message'}
      </Button>

      {error && <Fade in><Alert severity="error" sx={{ borderRadius: 2, mt: 2 }}>{error}</Alert></Fade>}

      {/* Result */}
      {result && (
        <Grow in timeout={400}>
          <Paper variant="outlined" sx={{ mt: 2.5, p: 2.5, borderRadius: 2 }}>
            <Alert severity="success" sx={{ borderRadius: 2, mb: 2 }}>Message sent successfully!</Alert>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={{ fontSize: '0.8rem', color: '#64748b', minWidth: 120 }}>Session ID</Typography>
                <Typography sx={{ fontSize: '0.85rem', fontFamily: 'monospace', color: '#334155' }}>{result.session_id}</Typography>
                <CopyBtn text={result.session_id} />
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={{ fontSize: '0.8rem', color: '#64748b', minWidth: 120 }}>Ticket Ref</Typography>
                <Typography sx={{ fontSize: '0.85rem', fontFamily: 'monospace', color: '#334155', wordBreak: 'break-all' }}>{result.jct_ticket_reference}</Typography>
                <CopyBtn text={result.jct_ticket_reference} />
              </Stack>
            </Stack>
            <Button variant="outlined" onClick={handleCheck} disabled={checking} startIcon={<Search size={14} />}
              sx={{ mt: 2, borderRadius: '10px', textTransform: 'none', fontWeight: 600, borderColor: '#e2e8f0', color: '#475569',
                '&:hover': { borderColor: '#0097A9', color: '#0097A9' } }}>
              {checking ? 'Checking…' : 'Check PNR Result'}
            </Button>
            {checkResult && (
              <Fade in>
                <Box sx={{ mt: 2 }}>
                  {checkResult.status?.toLowerCase() === 'confirmed' ? (
                    <Alert severity="success" sx={{ borderRadius: 2 }}>
                      <strong>Confirmed</strong> — PNR: <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{checkResult.pnr}</span>
                      <CopyBtn text={checkResult.pnr} />
                    </Alert>
                  ) : checkResult.status?.toLowerCase() === 'rejected' ? (
                    <Alert severity="error" sx={{ borderRadius: 2 }}><strong>Rejected</strong>{checkResult.service_error && ` — ${JSON.stringify(checkResult.service_error)}`}</Alert>
                  ) : checkResult.status === 'not_found' || checkResult.status === 'processing' ? (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      {checkResult.status === 'processing' ? 'Awaiting response from DHD engine. Try again in 30–90s.' : 'Not processed yet. Try again in 30–90s.'}
                    </Alert>
                  ) : checkResult.status === 'error' ? (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>{checkResult.error}</Alert>
                  ) : (
                    <Alert severity="warning" sx={{ borderRadius: 2 }}>Status: <strong>{checkResult.status}</strong></Alert>
                  )}
                </Box>
              </Fade>
            )}
          </Paper>
        </Grow>
      )}
    </Box>
  );
}

/* ══════════════════════════════════════
   MY BOOKINGS TAB
   ══════════════════════════════════════ */
function MyBookingsTab() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshingId, setRefreshingId] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const data = await pnrApi.listDhdBookings();
      setBookings(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRefresh = async (id) => {
    setRefreshingId(id);
    try {
      const res = await pnrApi.refreshDhdBooking(id);
      setBookings(prev => prev.map(b => b.id === id ? { ...b, status: res.status, pnr: res.pnr } : b));
    } catch { /* silent */ }
    setRefreshingId(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: '#0097A9' }} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>;
  }

  if (bookings.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8, color: '#94a3b8' }}>
        <PlaneTakeoff size={40} strokeWidth={1.5} />
        <Typography sx={{ mt: 2, fontWeight: 600, color: '#64748b' }}>No bookings yet</Typography>
        <Typography sx={{ fontSize: '0.85rem', color: '#94a3b8' }}>Send a DHD message to see it here</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: '0.85rem', color: '#64748b' }}>{bookings.length} booking{bookings.length !== 1 ? 's' : ''}</Typography>
        <Button size="small" startIcon={<RefreshCw size={14} />} onClick={load}
          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', color: '#475569' }}>
          Refresh All
        </Button>
      </Stack>

      <TableContainer sx={{ borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              {['Flight', 'Route', 'Date', 'Position', 'Type', 'Status', 'PNR', 'Created', ''].map(h => (
                <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.03em', py: 1.5, borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {bookings.map(b => (
              <TableRow key={b.id} sx={{ '&:hover': { bgcolor: '#f8fafc' }, '&:last-child td': { borderBottom: 0 } }}>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{b.flight_number || '—'}</TableCell>
                <TableCell>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 500 }}>{b.origin} → {b.destination}</Typography>
                </TableCell>
                <TableCell sx={{ fontSize: '0.8rem', color: '#475569' }}>{b.flight_date || '—'}</TableCell>
                <TableCell>
                  <Chip label={b.position} size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
                </TableCell>
                <TableCell>
                  <Chip label={b.request_type} size="small" sx={{ bgcolor: b.request_type === 'Update' ? '#fef3c7' : '#f0f9ff', color: b.request_type === 'Update' ? '#92400e' : '#0c4a6e', fontWeight: 600, fontSize: '0.7rem' }} />
                </TableCell>
                <TableCell>{statusChip(b.status)}</TableCell>
                <TableCell>
                  {b.pnr ? (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.85rem', color: '#16a34a' }}>{b.pnr}</Typography>
                      <CopyBtn text={b.pnr} />
                    </Stack>
                  ) : (
                    <Typography sx={{ fontSize: '0.8rem', color: '#94a3b8' }}>—</Typography>
                  )}
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                  {b.created_at ? new Date(b.created_at).toLocaleString() : '—'}
                </TableCell>
                <TableCell>
                  <Tooltip title="Fetch PNR from CosmosDB">
                    <IconButton size="small" onClick={() => handleRefresh(b.id)} disabled={refreshingId === b.id}>
                      {refreshingId === b.id ? <CircularProgress size={16} /> : <RefreshCw size={16} color="#64748b" />}
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

/* ══════════════════════════════════════
   MAIN PAGE
   ══════════════════════════════════════ */
export const DeadheadPnr = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [bookingsKey, setBookingsKey] = useState(0);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Header — matches Report Logs */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: 2, background: 'linear-gradient(135deg, #01426A 0%, #0097A9 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <PlaneTakeoff size={20} color="#fff" />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#01426A', lineHeight: 1.2 }}>DH PNR Generator</Typography>
          <Typography variant="caption" color="text.secondary">Create & track Deadhead PNR bookings</Typography>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }} elevation={0} variant="outlined">
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}
          sx={{
            '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', py: 1, minHeight: 42 },
            '& .Mui-selected': { color: '#01426A' },
            '& .MuiTabs-indicator': { bgcolor: '#0097A9', height: 3 },
          }}>
          <Tab label="Send DHD" icon={<Send size={16} />} iconPosition="start" />
          <Tab label="My Bookings" icon={<List size={16} />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Paper variant="outlined" sx={{ borderRadius: 2, p: { xs: 2, md: 3 } }}>
        {activeTab === 0 && <SendDhdTab onSent={() => setBookingsKey(k => k + 1)} />}
        {activeTab === 1 && <MyBookingsTab key={bookingsKey} />}
      </Paper>
    </Box>
  );
};

export default DeadheadPnr;
